import type {
    ActionItemPriority,
    ActionItemReasonLabel,
} from '@/features/action-items/types.ts';
import type { ActionItemEvidenceReviewLocator } from '@/features/action-items/draft-review-adapters/types.ts';
import {
    canonicalizeActionItemPatch,
    type ActionItemClearablePatchField,
    type ActionItemPatch,
} from '@/features/action-items/action-item-model.ts';

import {
    clearPendingActionItemMutation,
    markPendingActionItemMutation,
    type ActionItemProjection,
    type PendingActionItemMutation,
} from './action-item-pending-writes';
import { createProjectWriteOperation } from './project-write-operation-factory';
import { storeProjectWriteIntent } from './project-write-outbox';
import type { ProjectScope } from './project-scope';
import type { ProjectWriteOperation } from './write-operation';

export type ActionItemPatchClearField = ActionItemClearablePatchField;

type ActionItemCommandContext = {
    scope: ProjectScope;
    actorId: string;
    /** Construct this before its optimistic projection so event ids match the server write. */
    operation?: ProjectWriteOperation;
    optimistic: ActionItemProjection;
    prior: ActionItemProjection;
    acknowledgedItemIds?: string[];
    acknowledgedEventIds?: string[];
    acknowledgedEvidenceKeys?: string[];
    /** Restore the local projection if IndexedDB cannot durably retain this command. */
    onStorageFailure?: (operationId: string) => void;
};

export type CreateManualActionItemInput = ActionItemCommandContext & {
    actionItemId: string;
    title: string;
    reasonLabels: readonly ActionItemReasonLabel[];
    description?: string;
    dueDate?: string;
    priority?: ActionItemPriority;
    department?: string;
};

export type PromoteActionItemDraftInput = ActionItemCommandContext & {
    draftLocator: ActionItemEvidenceReviewLocator;
    evidenceKey: string;
    actionItemId: string;
    title: string;
    reasonLabels: readonly ActionItemReasonLabel[];
    description?: string;
    dueDate?: string;
    priority?: ActionItemPriority;
    department?: string;
    sanitizedSummary?: string;
};

export type DismissActionItemDraftInput = ActionItemCommandContext & {
    draftLocator: ActionItemEvidenceReviewLocator;
    evidenceKey: string;
    sanitizedSummary?: string;
};

export type PatchActionItemInput = ActionItemCommandContext & {
    actionItemId: string;
    patch: ActionItemPatch;
    clearFields: readonly ActionItemPatchClearField[];
};

type ActionItemCommand =
    | { operation: 'createManualActionItem'; actionItemId: string; title: string; reasonLabels: readonly ActionItemReasonLabel[]; description?: string; dueDate?: string; priority?: ActionItemPriority; department?: string }
    | { operation: 'promoteDraft'; draftLocator: ActionItemEvidenceReviewLocator; actionItemId: string; title: string; reasonLabels: readonly ActionItemReasonLabel[]; description?: string; dueDate?: string; priority?: ActionItemPriority; department?: string; sanitizedSummary?: string }
    | { operation: 'dismissDraft'; draftLocator: ActionItemEvidenceReviewLocator; sanitizedSummary?: string }
    | { operation: 'patchActionItem'; actionItemId: string; patch: PatchActionItemInput['patch']; clearFields: readonly ActionItemPatchClearField[] };

function commandConflictUnit(command: ActionItemCommand, input: ActionItemCommandContext): string {
    if (command.operation === 'promoteDraft' || command.operation === 'dismissDraft') {
        const reviewInput = input as PromoteActionItemDraftInput | DismissActionItemDraftInput;
        return `actionItemDraftReviews/${reviewInput.evidenceKey}`;
    }
    return `actionItems/${command.actionItemId}`;
}

function createPendingMutation(
    operationId: string,
    input: ActionItemCommandContext,
): PendingActionItemMutation {
    return {
        operationId,
        optimistic: input.optimistic,
        prior: input.prior,
        acknowledgedItemIds: input.acknowledgedItemIds ?? input.optimistic.items.map(item => item.id),
        acknowledgedEventIds: input.acknowledgedEventIds ?? input.optimistic.events.map(event => event.id),
        acknowledgedEvidenceKeys: input.acknowledgedEvidenceKeys ?? input.optimistic.reviewedEvidenceKeys,
    };
}

async function storeActionItemCommand(
    input: ActionItemCommandContext,
    command: ActionItemCommand,
): Promise<ProjectWriteOperation> {
    const base = input.operation ?? createProjectWriteOperation({
        scope: input.scope,
        actorId: input.actorId,
        feature: 'action_items',
        capability: 'EDIT_ACTION_ITEMS',
        conflictUnit: commandConflictUnit(command, input),
        kind: 'sideEffectCommand',
        payload: null,
    });
    const operation: ProjectWriteOperation = {
        ...base,
        payload: {
            commandType: command.operation === 'promoteDraft'
                ? 'promoteActionItemDraft'
                : command.operation === 'dismissDraft'
                    ? 'dismissActionItemDraft'
                    : command.operation,
            clientMutationId: base.id,
            command,
        },
    };

    markPendingActionItemMutation(input.scope, createPendingMutation(operation.id, input));
    try {
        await storeProjectWriteIntent(operation);
        return operation;
    } catch (error) {
        try {
            input.onStorageFailure?.(operation.id);
        } finally {
            clearPendingActionItemMutation(input.scope, operation.id);
        }
        throw error;
    }
}

/**
 * Allocates the durable operation identity before the caller builds its local
 * projection.  Server-created Action Item events are keyed from this id.
 */
export function createActionItemWriteOperation(
    scope: ProjectScope,
    actorId: string,
    conflictUnit: string,
): ProjectWriteOperation {
    return createProjectWriteOperation({
        scope,
        actorId,
        feature: 'action_items',
        capability: 'EDIT_ACTION_ITEMS',
        conflictUnit,
        kind: 'sideEffectCommand',
        payload: null,
    });
}

export function createManualActionItem(input: CreateManualActionItemInput): Promise<ProjectWriteOperation> {
    return storeActionItemCommand(input, {
        operation: 'createManualActionItem',
        actionItemId: input.actionItemId,
        title: input.title,
        reasonLabels: input.reasonLabels,
        ...(input.description !== undefined ? { description: input.description } : {}),
        ...(input.dueDate !== undefined ? { dueDate: input.dueDate } : {}),
        ...(input.priority !== undefined ? { priority: input.priority } : {}),
        ...(input.department !== undefined ? { department: input.department } : {}),
    });
}

export function promoteActionItemDraft(input: PromoteActionItemDraftInput): Promise<ProjectWriteOperation> {
    return storeActionItemCommand(input, {
        operation: 'promoteDraft', draftLocator: input.draftLocator,
        actionItemId: input.actionItemId, title: input.title, reasonLabels: input.reasonLabels,
        ...(input.description !== undefined ? { description: input.description } : {}),
        ...(input.dueDate !== undefined ? { dueDate: input.dueDate } : {}),
        ...(input.priority !== undefined ? { priority: input.priority } : {}),
        ...(input.department !== undefined ? { department: input.department } : {}),
        ...(input.sanitizedSummary !== undefined ? { sanitizedSummary: input.sanitizedSummary } : {}),
    });
}

export function dismissActionItemDraft(input: DismissActionItemDraftInput): Promise<ProjectWriteOperation> {
    return storeActionItemCommand(input, {
        operation: 'dismissDraft', draftLocator: input.draftLocator,
        ...(input.sanitizedSummary !== undefined ? { sanitizedSummary: input.sanitizedSummary } : {}),
    });
}

export function patchActionItem(input: PatchActionItemInput): Promise<ProjectWriteOperation> {
    const canonical = canonicalizeActionItemPatch(input.patch, input.clearFields);
    return storeActionItemCommand(input, {
        operation: 'patchActionItem', actionItemId: input.actionItemId,
        patch: canonical.patch, clearFields: canonical.clearFields,
    });
}
